import { cn } from "@/lib/utils";

/**
 * A night map of a city that is nowhere in particular: a street grid that breaks into blocks, a
 * few avenues across it, a river and two parks tinted with the brand colour. Drawn, not tiled, so
 * the feature pages make no map requests and never show somebody's real street as an example.
 */

/**
 * The same "random" breaks on every render, so the server and the browser draw one map. Rounded,
 * because `Math.sin` can differ in its last digits between Node and a browser.
 */
const rand = (seed: number) => {
  const x = Math.sin(seed * 12.9898) * 43_758.5453;
  return x - Math.floor(x);
};

/** A street that runs the whole grid with gaps, which is what makes the grid read as city blocks. */
const breaks = (seed: number) =>
  Array.from({ length: 6 }, (_, index) => [
    Math.round(40 + rand(seed + index) * 220),
    Math.round(18 + rand(seed * 3 + index) * 60),
  ])
    .flat()
    .join(" ");

const STREETS = Array.from({ length: 30 }, (_, index) => index * 36 - 260);

export const CityMap = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 800 560" preserveAspectRatio="xMidYMid slice" aria-hidden className={cn("size-full", className)}>
    <rect width="800" height="560" className="fill-[color-mix(in_oklch,var(--foreground)_3%,var(--background))]" />
    <g className="fill-[color-mix(in_oklch,var(--primary)_11%,var(--background))]">
      <path d="M520 40c60-10 130 10 150 60s-20 100-80 106-110-20-120-70 0-88 50-96Z" />
      <path d="M90 360c50-14 110 6 118 52s-30 84-86 88-96-26-98-70 20-60 66-70Z" />
    </g>
    <path
      d="M-20 250C90 230 170 300 270 300s170-90 280-100 160 60 270 40"
      className="fill-none stroke-[color-mix(in_oklch,var(--foreground)_7%,var(--background))]"
      strokeWidth="30"
      strokeLinecap="round"
    />
    <g
      transform="rotate(-17 400 280)"
      className="fill-none stroke-[color-mix(in_oklch,var(--foreground)_9%,var(--background))]"
      strokeWidth="4"
    >
      {STREETS.map((at, index) => (
        <line key={`row-${at}`} x1="-300" y1={at} x2="1100" y2={at} strokeDasharray={breaks(index + 1)} />
      ))}
      {STREETS.map((at, index) => (
        <line key={`column-${at}`} x1={at + 200} y1="-300" x2={at + 200} y2="900" strokeDasharray={breaks(index + 40)} />
      ))}
    </g>
    <g className="fill-none stroke-[color-mix(in_oklch,var(--foreground)_17%,var(--background))]" strokeLinecap="round">
      <path d="M-20 120C180 150 330 90 520 130s220 110 300 100" strokeWidth="9" />
      <path d="M230 -20c20 140-30 260 10 380s-10 180 0 220" strokeWidth="9" />
      <path d="M-20 470c140-40 300-20 430-90s250-120 410-110" strokeWidth="7" />
      <path d="M600 -20c-30 120 40 210 10 320s30 170 40 280" strokeWidth="6" />
    </g>
  </svg>
);
